import { DataType, FormDialogMode, SelectOption, useNotify } from '@globaleris/ui.library'
import { Button, Grid } from '@material-ui/core'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { EnumCommodity, EnumCurrency } from '../../../api'
import { EnumClientTypes } from '../../../api/Argentia/enums/enumClientTypes'
import { EnumProrrateo } from '../../../api/Argentia/enums/enumProrrateo'
import {
  LanguageModel,
  ProductConceptModel,
  ProductModel
} from '../../../api/Argentia/model/contracts'
import { EnumTipoRelacionProductoConcepto } from '../../../api/oData/enums/enumTipoRelacionProductoConcepto'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import PageLeftColumn from '../../../components/page/pageLeftColumn'
import { enumToSelectOptionArray, enumToSelectOptionArrayDefaultValues } from '../../../helpers'
import { getFilteredBreakdownOptions } from '../../../helpers/breakdownOptionsHelper'
import { FormDialogDefinitionProps, useFormDialog } from '../../../hooks'
import { useConfirmationDialog } from '../../../hooks'
import { useDropdownOptions } from '../../../hooks/useDropdownOptions'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import TemplateSettings from '../../templateSettings/templateSettings.model'
import { mapLanguagesResponse } from '../add/products.add.mapper'
import {
  addProductToTemplateSettings,
  deleteProductFromTemplateSettings,
  filterSelectedTemplates
} from '../products.helpers'
import {
  mapProductModel,
  mapTemplates,
  mapTemplateToArray,
  mapTemplateToSelectOption
} from '../products.mapper'
import {
  fetchLanguages,
  fetchProductById,
  fetchProductTypeOptionsByClientType,
  fetchTemplates,
  fetchTemplatesByProductId,
  fetchWeightedTypeListByBreakdownType,
  postTemplateUpdate,
  updateProduct
} from '../products.service'
import { ConceptCard } from './components/ConceptCard/conceptCard'
import { ProductDetail } from './components/productDetail/productDetail'
import { TranslationsCard } from './components/translationsCard/translationsCard'
import { mapTranslationsToTranslationsModel } from './components/translationsCard/translationsCard.mapper'
import { ProductTranslationModel } from './components/translationsCard/translationsCard.model'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { updateListadoConceptosByProductId } from '../../templateSettings/templateSettings.service'
export interface ProductEditPageProps {
  location: {
    state: {
      productId: number
      clientType: EnumClientTypes
    }
  }
}

export const ProductsEditPage: React.FC<ProductEditPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'productsEdit', 'products', 'enums'])
  const { info, error } = useNotify()
  const history = useHistory()
  const [productDetail, setProductDetail] = useState<ProductModel | undefined>({} as ProductModel)
  const [productTypeOptions, setProductTypeOptions] = useState<SelectOption[] | undefined>([])
  const [breakdownOptions, setBreakdownOptions] = useState<SelectOption[] | undefined>([])
  const [templates, setTemplates] = useState<TemplateSettings[]>([])
  const [productTemplates, setProductTemplates] = useState<SelectOption[] | undefined>([])
  const [productTemplatesConcepts, setProductTemplatesConcepts] = useState<
    SelectOption[] | undefined
  >([])
  const [templatesOption, setTemplatesOptions] = useState<SelectOption[] | undefined>([])
  const [templateConceptsOption, setTemplateConceptsOptions] = useState<SelectOption[] | undefined>(
    []
  )
  const [languages, setLanguages] = useState<LanguageModel[]>([])
  const [isFetchingProductDetails, setIsFetchingProductDetails] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasUnsavedConceptChanges, setHasUnsavedConceptChanges] = useState<boolean>(false)
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const {
    openForm,
    updateFormDefinition,
    updateFormData,
    closeForm,
    formProps,
    CustomFormDialog,
    setFormWaiting
  } = useFormDialog()
  const weightedTypeSelectOption = useDropdownOptions<number[], [number]>(
    fetchWeightedTypeListByBreakdownType
  )
  const clientType = props?.location?.state?.clientType
    ? props.location.state.clientType
    : EnumClientTypes.FINAL_CLIENT

  const productHasConcepts = useCallback((): boolean => {
    if (productDetail?.relatedConcepts != undefined && productDetail?.relatedConcepts?.length > 0) {
      return true
    } else {
      return false
    }
  }, [productDetail])

  //#region Handlers Starts

  //#region Header Handlers Starts
  const handleGoBack = () => {
    history.push(NonAuthRoutes.Products, {
      clientType: clientType
    })
  }

  const onEditProductDetails = () => {
    preloadFormSelectOptions()
    openForm({
      definition: editFormDefinition,
      title: t('productsEdit:forms.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: {
        ...productDetail,
        templates: productTemplates ? mapTemplateToArray(productTemplates) : [],
        templateConcepts: productTemplatesConcepts
          ? mapTemplateToArray(productTemplatesConcepts)
          : []
      },
      onClose: onEditProductDetailCloseHandler
    })
  }

  const preloadFormSelectOptions = () => {
    if (productDetail?.commodity) {
      const newOptions = getFilteredBreakdownOptions(productDetail.commodity)
      setBreakdownOptions(newOptions)
    }
    if (productDetail?.breakdownType) {
      weightedTypeSelectOption.fetchData(productDetail.breakdownType)
    }
  }

  const onEditProductDetailCloseHandler = async formData => {
    if (formData) {
      if (missingFieldsFormWithRequiredText(editFormDefinition?.sections!, formData)) {
        error(t('productsEdit:forms.edit.notifications.save.errorInvalidForm'))
        return
      }
      // Map data from CustomFormDialog (Modal) separating Product Details from Product Templates
      const {
        templates: editedTemplate,
        templateConcepts: editedTemplateConcepts,
        ...rest
      } = formData

      const newProductDetail: ProductModel = {
        ...productDetail,
        ...rest
      } as ProductModel

      onSaveProduct(newProductDetail, [...editedTemplate, ...editedTemplateConcepts])
    }
    closeForm()
  }

  const onSaveProduct = async (
    newProduct: ProductModel,
    editedTemplates?: number[] | undefined,
    redirect?
  ): Promise<void> => {
    setIsLoading(true)
    // Generate update request
    await updateProduct(newProduct).then(async response => {
      if (response.success) {
        info(t('productsEdit:notifications.save.success'))
        setHasUnsavedConceptChanges(false)
        // Update listado conceptos after editing concept
        if (productDetail?.id) {
          await updateListadoConceptosByProductId(newProduct.id!)
        }
        if (editedTemplates) {
          // update templates if input was used

          // filter templates to have shorter array (performance)
          const selectedTemplatesSettings: TemplateSettings[] | undefined = filterSelectedTemplates(
            templates,
            editedTemplates
          )

          // Add productId to new selected templates
          const templateSettingsAddedOrEqual = addProductToTemplateSettings(
            selectedTemplatesSettings,
            newProduct.id!,
            newProduct.description
          )

          // Delete productId from old templates
          const oldTemplatesId = [...productTemplates!, ...productTemplatesConcepts!].map(
            t => t.value as number
          )

          // get deleted templates
          const deletedTemplatesId = oldTemplatesId.filter(t => !editedTemplates.includes(t))

          // filter templates to have shorter array (performance)
          const templateSettingsToDelete: TemplateSettings[] | undefined = filterSelectedTemplates(
            templates,
            deletedTemplatesId
          )

          const templateSettingsDeleted = deleteProductFromTemplateSettings(
            templateSettingsToDelete,
            newProduct.id!
          )

          // Get all templates to Update
          const templateSettingsUpdated = [
            ...templateSettingsAddedOrEqual,
            ...templateSettingsDeleted
          ]

          // POST update
          const success: boolean[] = [] //array made to store all post status responses

          await Promise.all(
            // POST each Selected Template with newProduct Id received from createProduct call
            templateSettingsUpdated.map(async template => {
              await postTemplateUpdate(template).then(response => {
                if (response.success) {
                  success.push(true)
                } else {
                  success.push(false)
                }
              })
            })
          ).finally(() => {
            if (success.includes(false)) {
              error(t('productsEdit:notifications.save.errorTemplates'))
            } else {
              info(t('productsEdit:notifications.save.successTemplates'))
            }
            setIsLoading(false)
          })
        }
        if (redirect) {
          redirect(newProduct)
        } else {
          fetchProductDetails() // update product after saving
        }
      } else {
        error(t('productsEdit:notifications.save.error'))
        setIsLoading(false)
      }
    })
  }

  //#endregion Header Handlers Ends

  //#region TranslationCards Handlers Starts

  const handleAddTranslationsRow = (formData: ProductTranslationModel) => {
    const newProductDetail: ProductModel = { ...productDetail! }

    const newTranslation = {
      description: formData.description,
      language: formData.language,
      tempId: formData.tempId
    } as ProductTranslationModel

    newProductDetail.translations = [newTranslation, ...newProductDetail?.translations!]
    setProductDetail(newProductDetail)
  }

  const handleEditTranslationsRow = (rowData: ProductTranslationModel) => {
    const newProductDetail: ProductModel = { ...productDetail! }

    let property: number | string

    if (rowData?.id !== undefined) property = 'id'
    else property = 'tempId'

    const newTranslation = newProductDetail?.translations?.find(
      e => e[property] === rowData[property]
    )

    if (newTranslation) {
      newTranslation.description = rowData.description
      newTranslation.language = rowData.language
      newTranslation.tempId = rowData.tempId ? rowData.tempId : newTranslation.tempId
    }

    setProductDetail(newProductDetail)
  }

  const handleDeleteTranslationsRow = (rowData: ProductTranslationModel) => {
    let property: string

    if (rowData?.id !== undefined) property = 'id'
    else property = 'tempId'

    const newTranslation = productDetail?.translations?.filter(
      e => e[property] !== rowData[property]
    )

    setProductDetail(prevState => {
      if (prevState) {
        return {
          ...prevState,
          translations: newTranslation
        } as ProductModel
      }
      return prevState
    })
  }

  //#endregion TranslationCards Handlers Ends

  //#region Concepts Handlers Starts

  // Obligatory, Optional and Conditional Concepts are handled the same way. They are only filtered in different cards by "required" property
  const handleAddConceptsRow = (newProductConcepts: ProductConceptModel[]) => {
    setHasUnsavedConceptChanges(true)
    setProductDetail(prevState => {
      // handle productDetail = null | undefined
      if (prevState) {
        // handle relatedConcepts being null | undefined (empty)
        if (prevState.relatedConcepts) {
          return {
            ...prevState,
            relatedConcepts: [...newProductConcepts, ...prevState.relatedConcepts]
          }
        } else {
          return {
            ...prevState,
            relatedConcepts: [...newProductConcepts]
          }
        }
      }
    })
  }

  const handleEditConceptsRow = (editedProductConcept: ProductConceptModel) => {
    setHasUnsavedConceptChanges(true)
    // map productConcepts and replace editedProduct
    const newRelatedConcepts = productDetail?.relatedConcepts!.map(concept => {
      if (concept.conceptId === editedProductConcept.conceptId) {
        return editedProductConcept
      } else {
        return concept
      }
    })

    // setting new state
    setProductDetail(prevState => {
      // handle productDetail = null | undefined
      if (prevState) {
        return {
          ...prevState,
          relatedConcepts: [...newRelatedConcepts!]
        }
      }
    })
  }

  const handleDeleteConceptsRow = (conceptId: number, tempId: string | undefined) => {
    setHasUnsavedConceptChanges(true)
    // filter all elements having conceptId or tempId
    const newRelatedConcepts = productDetail?.relatedConcepts!.filter(e => {
      if (conceptId != undefined && e.conceptId === conceptId) {
        return false
      } else if (tempId != undefined && e.tempId === tempId) {
        return false
      } else {
        return true
      }
    })

    setProductDetail(prevState => {
      // handle productDetail = null | undefined
      if (prevState) {
        return {
          ...prevState,
          relatedConcepts: [...newRelatedConcepts!] // deleting operation assures at least one related concept must exist
        }
      }
    })
  }

  //#endregion Concepts Handlers Ends

  const handleViewConceptDetail = async (rowData: ProductConceptModel) => {
    if (hasUnsavedConceptChanges) {
      await getConfirmation(ConfirmationDialogs.DISCARD_CHANGES).then(() => {
        closeConfirmation()
        navigateToConceptDetail(rowData)
      })
    } else {
      navigateToConceptDetail(rowData)
    }
  }

  const navigateToConceptDetail = (rowData: ProductConceptModel) => {
    history.push(NonAuthRoutes.ConceptsEdit, {
      clientType: clientType,
      calculationType: rowData.calculationType,
      conceptId: rowData.conceptId,
      commodity: rowData.commodity ?? productDetail?.commodity
    })
  }

  //#endregion Handlers Ends

  //#region Form Definition Starts

  const handleCommodityChange = (rawValue: any, formState: any) => {
    console.log(rawValue)
    const commodityValue =
      rawValue === '' || rawValue === null || rawValue === undefined
        ? undefined
        : (Number(rawValue) as EnumCommodity)
    const newOptions = getFilteredBreakdownOptions(commodityValue)
    console.log(commodityValue)

    console.log(newOptions)
    setBreakdownOptions(commodityValue ? newOptions : [])
    weightedTypeSelectOption.reset()
  }

  const editFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT',
        sections: [
          {
            inputs: [
              {
                name: 'code',
                label: t('productsEdit:forms.edit.inputs.code'),
                type: DataType.TEXT,
                required: true
              },
              {
                name: 'description',
                label: t('productsEdit:forms.edit.inputs.description'),
                type: DataType.TEXT,
                readOnly: true,
                helpText: '* ' + t('productsEdit:forms.edit.inputs.descriptionEditHelper')
              },
              {
                name: 'commodity',
                label: t('productsEdit:forms.edit.inputs.commodity'),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(EnumCommodity, 'enums:EnumCommodity'),
                clearable: true,
                onChange: (value, formState) => {
                  updateFormData({
                    ...formState,
                    breakdownType: undefined,
                    weightedType: undefined
                  })
                  if (value === null || value === '' || value === undefined) {
                    setBreakdownOptions([])
                    weightedTypeSelectOption.reset()
                  } else {
                    handleCommodityChange(value, formState)
                  }
                }
              },
              {
                name: 'type',
                label: t('productsEdit:forms.edit.inputs.type'),
                type: DataType.SELECT,
                selectOptions: productTypeOptions,
                readOnly: true
              },
              {
                name: 'currency',
                label: t('productsEdit:forms.edit.inputs.currency'),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(EnumCurrency, 'enums:EnumCurrencyLabel'),
                readOnly: productHasConcepts()
              },
              {
                name: 'breakdownType',
                label: t('productsEdit:forms.edit.inputs.breakdownType'),
                type: DataType.SELECT,
                selectOptions: breakdownOptions,
                clearable: true,
                required: true,
                onChange: (value, formState) => {
                  const breakdownTypeId = +value
                  updateFormData({
                    ...formState,
                    weightedType: undefined
                  })
                  if (!breakdownTypeId) {
                    weightedTypeSelectOption.reset()
                  } else {
                    weightedTypeSelectOption.fetchData(breakdownTypeId)
                  }
                }
              },
              {
                name: 'weightedType',
                label: t('productsEdit:forms.edit.inputs.weightedType'),
                type: DataType.SELECT,
                selectOptions:
                  breakdownOptions && breakdownOptions?.length > 0
                    ? enumToSelectOptionArrayDefaultValues(
                        EnumProrrateo,
                        'enums:EnumProrrateo',
                        weightedTypeSelectOption.responseData
                      )
                    : [],
                clearable: true,
                required: true,
                isLoading: weightedTypeSelectOption.isLoading
              },
              {
                name: 'templates',
                label: t('productsEdit:forms.edit.inputs.templates'),
                type: DataType.MULTISELECT,
                selectOptions: templatesOption,
                clearable: true
              },
              {
                name: 'templateConcepts',
                label: t('productsEdit:forms.edit.inputs.templateConcepts'),
                type: DataType.MULTISELECT,
                selectOptions: templateConceptsOption,
                clearable: true
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      productTypeOptions,
      productHasConcepts,
      templatesOption,
      templateConceptsOption,
      productTemplates,
      productTemplatesConcepts,
      weightedTypeSelectOption.isLoading,
      weightedTypeSelectOption.responseData,
      breakdownOptions
    ])

  //#endregion Form Definition Ends

  // DataTable Config Starts
  // DataTable Config Ends

  // Functions Starts

  const fetchProductDetails = async () => {
    setIsFetchingProductDetails(true)
    setHasUnsavedConceptChanges(false)
    await Promise.all([
      await fetchProductById(props.location.state.productId).then(response => {
        if (response.success) {
          const newProduct = mapProductModel(response.data)
          setProductDetail(newProduct)
          setBreakdownOptions(getFilteredBreakdownOptions(newProduct.commodity))
        } else {
          error(t('productsEdit:notifications.load.productError'))
        }
      }),
      await fetchProductTypeOptionsByClientType(clientType).then(response => {
        if (response.success) {
          setProductTypeOptions(response.data)
        } else {
          error(t('productsEdit:notifications.load.productTypeError'))
        }
      }),
      await fetchTemplates().then(response => {
        if (response.success) {
          setTemplates(response.data as TemplateSettings[]) // handle all template data
          const { templatesOnly, templateConceptsOnly } = mapTemplates(response.data) // filter template data from usecase (templates & Concepts only)
          setTemplatesOptions(mapTemplateToSelectOption(templatesOnly)) // map template data to Form SelectOption
          setTemplateConceptsOptions(mapTemplateToSelectOption(templateConceptsOnly)) // map template data to Form SelectOption
        } else {
          error(t('productsEdit:notifications.load.errorGetTemplates'))
        }
      }),
      await fetchTemplatesByProductId(props.location.state.productId).then(response => {
        if (response.success) {
          const { templatesOnly, templateConceptsOnly } = mapTemplates(response.data) // filter template data from usecase (templates & Concepts only)
          setProductTemplates(mapTemplateToSelectOption(templatesOnly)) // map template data to Form SelectOption
          setProductTemplatesConcepts(mapTemplateToSelectOption(templateConceptsOnly)) // map template data to Form SelectOption
        } else {
          error(t('productsEdit:notifications.load.errorGetProductTemplates'))
        }
      }),
      await fetchLanguages().then(response => {
        if (response.success) {
          setLanguages(mapLanguagesResponse(response.data))
        } else {
          error(t('products:notifications.FetchLanguages.error'))
          return []
        }
      })
    ]).finally(() => {
      setIsFetchingProductDetails(false)
    })
  }

  // Functions Ends

  // Hooks Starts
  useEffect(() => {
    fetchProductDetails()
  }, [])

  // useEffect(() => {
  //   setBreakdownOptions(getFilteredBreakdownOptions(productDetail?.commodity))
  // }, [productDetail?.commodity])

  useEffect(() => {
    updateFormDefinition(editFormDefinition)
  }, [editFormDefinition])

  useEffect(() => {
    if (weightedTypeSelectOption.responseError) {
      error(t('productsEdit:notifications.load.errorGetWeightedType'))
    }
  }, [weightedTypeSelectOption.responseError])

  // Hooks Ends

  return (
    <>
      {!isLoading && (
        <Page layout={PageLayout.MULTI_COLUMN}>
          <PageLeftColumn>
            <ProductDetail
              product={productDetail}
              productTypeOptions={productTypeOptions}
              templates={templates}
              templatesOptions={templatesOption}
              templateConceptsOptions={templateConceptsOption}
              productTemplates={productTemplates}
              productTemplatesConcepts={productTemplatesConcepts}
              isLoading={isFetchingProductDetails}
            />
          </PageLeftColumn>
          <PageCenterColumn>
            <PageHeader title={t('productsEdit:title')}>
              <Button onClick={handleGoBack} size="small" color="primary">
                {t('productsEdit:mainActions.back')}
              </Button>
              <Button onClick={onEditProductDetails} size="small" color="primary">
                {t('productsEdit:mainActions.editProductDetails')}
              </Button>
              <Button
                variant="contained"
                onClick={() =>
                  onSaveProduct(productDetail!, undefined, () => {
                    history.push(NonAuthRoutes.Products, {
                      clientType: clientType
                    })
                  })
                }
                size="small"
                color="primary"
              >
                {t('productsEdit:mainActions.saveProduct')}
              </Button>
            </PageHeader>
            <CustomFormDialog {...formProps}></CustomFormDialog>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <ConceptCard
                  filterByRelationType={EnumTipoRelacionProductoConcepto.Obligatorio}
                  productDetail={productDetail}
                  clientType={clientType}
                  onAddConceptsRowValues={newProductConcepts =>
                    handleAddConceptsRow(newProductConcepts)
                  }
                  onEditConceptsRowValues={editedProductConcept =>
                    handleEditConceptsRow(editedProductConcept)
                  }
                  onDeleteConceptsRowValues={(conceptId, tempId) =>
                    handleDeleteConceptsRow(conceptId, tempId)
                  }
                  onViewConceptDetail={handleViewConceptDetail}
                  isFetching={isFetchingProductDetails}
                />
              </Grid>
              <Grid item>
                <ConceptCard
                  filterByRelationType={EnumTipoRelacionProductoConcepto.Opcional}
                  productDetail={productDetail}
                  clientType={clientType}
                  onAddConceptsRowValues={newProductConcepts =>
                    handleAddConceptsRow(newProductConcepts)
                  }
                  onEditConceptsRowValues={editedProductConcept =>
                    handleEditConceptsRow(editedProductConcept)
                  }
                  onDeleteConceptsRowValues={(conceptId, tempId) =>
                    handleDeleteConceptsRow(conceptId, tempId)
                  }
                  onViewConceptDetail={handleViewConceptDetail}
                  isFetching={isFetchingProductDetails}
                />
              </Grid>
              <Grid item>
                <ConceptCard
                  filterByRelationType={EnumTipoRelacionProductoConcepto.Condicional}
                  productDetail={productDetail}
                  clientType={clientType}
                  onAddConceptsRowValues={newProductConcepts =>
                    handleAddConceptsRow(newProductConcepts)
                  }
                  onEditConceptsRowValues={editedProductConcept =>
                    handleEditConceptsRow(editedProductConcept)
                  }
                  onDeleteConceptsRowValues={(conceptId, tempId) =>
                    handleDeleteConceptsRow(conceptId, tempId)
                  }
                  onViewConceptDetail={handleViewConceptDetail}
                  isFetching={isFetchingProductDetails}
                />
              </Grid>
              <Grid item>
                <TranslationsCard
                  translations={mapTranslationsToTranslationsModel(productDetail?.translations)}
                  languages={languages}
                  onAddTranslationsRowValues={formData => handleAddTranslationsRow(formData)}
                  onEditTranslationsRowValues={rowData => handleEditTranslationsRow(rowData)}
                  onDeleteTranslationsRowValues={rowData => handleDeleteTranslationsRow(rowData)}
                  isFetching={isFetchingProductDetails}
                />
              </Grid>
            </Grid>
          </PageCenterColumn>
          {/* <PageRightColumn></PageRightColumn> */}
        </Page>
      )}
    </>
  )
}
